import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiZap, FiCheckCircle, FiAlertCircle, FiChevronRight, FiShield } from "react-icons/fi";
import { getStoreModules, buyStoreModule } from "../../services/api";
import { getTokenStats } from "../../services/tokenService";
import Footer from "../../components/footer";
import ModuleHeader from "../../components/moduleHeader";
import Notiflix from "notiflix";
import { FaCoins } from "react-icons/fa";

export default function StoreModuleDetail() {
  const { id, packageId } = useParams();
  const navigate = useNavigate();
  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });

    // Confirm popup buton renklerini özelleştir
    Notiflix.Confirm.init({
      okButtonBackground: "#22c55e", // Yeşil
      cancelButtonBackground: "#374151", // Koyu gri
      okButtonColor: "#ffffff",
      cancelButtonColor: "#ffffff",
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Modül bilgilerini al
        const storeResponse = await getStoreModules();
        if (storeResponse && storeResponse.success && storeResponse.data) {
          // API'den direkt packages array'i geliyor
          const foundModule = storeResponse.data.packages.find(pkg => pkg.package_id === parseInt(packageId));
          if (foundModule) {
            setModuleData(foundModule);
          }
        }

        // Token bakiyesini al
        try {
          const tokenStatsResponse = await getTokenStats();
          if (tokenStatsResponse && tokenStatsResponse.success && tokenStatsResponse.stats) {
            const balance = parseFloat(tokenStatsResponse.stats.current_balance) || 0;
            setTokenBalance(balance);
          }
        } catch (error) {
          console.error("Token bakiyesi alınırken hata:", error);
        }
      } catch (error) {
        console.error("Veri alınırken hata:", error);
        Notiflix.Notify.failure("Modül bilgileri yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [packageId]);

  const handleBuy = async () => {
    if (!moduleData) return;

    if (moduleData.is_owned) {
      Notiflix.Notify.info("Bu modüle zaten sahipsiniz.");
      return;
    }

    if (tokenBalance < moduleData.jodiac_token_cost) {
      Notiflix.Notify.warning("Yeterli Jodiac Token'ınız bulunmuyor.");
      return;
    }

    Notiflix.Confirm.show(
      "Satın Al",
      `${moduleData.name} modülünü ${moduleData.jodiac_token_cost} JT karşılığında satın almak istediğinize emin misiniz?`,
      "Satın Al",
      "İptal",
      async () => {
        try {
          setBuying(true);
          const response = await buyStoreModule(moduleData.package_id);

          if (response && response.success) {
            Notiflix.Notify.success(response.message || "Modül başarıyla satın alındı!");

            // Token bakiyesini güncelle
            if (response.data && response.data.remaining_balance !== undefined) {
              setTokenBalance(response.data.remaining_balance);
            }

            // Modül durumunu güncelle
            setModuleData(prev => ({ ...prev, is_owned: true }));

            // 2 saniye sonra modül sayfasına yönlendir
            setTimeout(() => {
              navigate(`/${id}/${moduleData.package_id}`);
            }, 2000);
          }
        } catch (error) {
          console.error("Satın alma hatası:", error);
          const errorMessage = error.response?.data?.message || "Satın alma işlemi başarısız oldu.";
          Notiflix.Notify.failure(errorMessage);
        } finally {
          setBuying(false);
        }
      },
      () => { } // Cancel callback
    );
  };

  if (loading) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  if (!moduleData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#fff]">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Modül bulunamadı.</p>
          <button
            onClick={() => navigate(`/${id}/store`)}
            className="px-4 py-2 bg-[#3DA7B5] text-white rounded-lg"
          >
            Mağazaya Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen min-h-screen flex flex-col items-center bg-[#F8F9FB] overflow-hidden">
      <div className="flex flex-col items-center w-full h-full sm:max-w-[390px] bg-white sm:rounded-[40px] shadow-2xl relative overflow-y-auto no-scrollbar">

        {/* ModuleHeader - Preserved as requested */}
        <div className="relative z-30 w-full mb-2">
          <ModuleHeader
            subtitle="Modül Detayı"
            title={moduleData.name}
            description={moduleData.description || "Bu modül hakkında detaylı bilgi."}
            backgroundImage={moduleData.image_url}
            onExpandedChange={setIsHeaderExpanded}
            showToggle={true}
            autoHeight={true}
          />
        </div>

        {/* Dynamic Content Container */}
        <div className={`w-full px-6 flex-1 bg-white relative z-20 transition-all duration-300 ${isHeaderExpanded ? "mt-0" : "-mt-6 rounded-t-[40px] shadow-[0_-20px_40px_rgba(0,0,0,0.05)]"}`}>
          <div className="pt-8 pb-32 space-y-6">

            {/* Balance Card - Modern Gradient Style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative overflow-hidden bg-gradient-to-br from-[#379cab]/5 to-[#2a8a96]/5 border border-[#379cab]/10 rounded-3xl p-5"
            >
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#379cab]/10 rounded-full blur-3xl opacity-50" />
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#379cab] flex items-center justify-center text-white shadow-lg shadow-[#379cab]/20">
                    <FaCoins size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-black text-[#379cab] uppercase tracking-wider">Cüzdanın</span>
                    <span className="text-sm font-bold text-gray-700">Mevcut Bakiye</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xl font-black text-[#379cab]">{tokenBalance} JT</span>
                </div>
              </div>
            </motion.div>

            {/* Main Purchase Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white border border-gray-100 rounded-[2rem] p-6 shadow-xl shadow-gray-200/20"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Satın Alma Bedeli</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-gray-900">{moduleData.jodiac_token_cost}</span>
                    <span className="text-sm font-bold text-[#379cab]">JT</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-[#379cab]">
                  <FiZap className="fill-[#379cab]" size={24} />
                </div>
              </div>

              {moduleData.is_owned ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-2xl border border-green-100">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white shadow-lg shadow-green-500/20">
                      <FiCheckCircle size={16} />
                    </div>
                    <p className="text-sm font-bold text-green-700">Bu modüle zaten sahipsin.</p>
                  </div>
                  <button
                    onClick={() => navigate(`/${id}/${moduleData.package_id}`)}
                    className="w-full py-4 bg-[#379cab] text-white rounded-2xl font-black text-sm shadow-xl shadow-[#379cab]/20 hover:bg-[#2a8a96] transition-all active:scale-95 flex items-center justify-center gap-2"
                  >
                    Hemen Kullanmaya Başla <FiChevronRight />
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {tokenBalance >= moduleData.jodiac_token_cost ? (
                    <button
                      onClick={handleBuy}
                      disabled={buying}
                      className="w-full py-4 bg-[#379cab] text-white rounded-2xl font-black text-lg shadow-xl shadow-[#379cab]/20 hover:bg-[#2a8a96] transition-all active:scale-95 disabled:opacity-50"
                    >
                      {buying ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          İşlem Yapılıyor...
                        </div>
                      ) : "Satın Alımı Onayla"}
                    </button>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-4 bg-red-50 rounded-2xl border border-red-100">
                        <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white">
                          <FiAlertCircle size={16} />
                        </div>
                        <p className="text-sm font-bold text-red-700">Eksik Bakiyen: {moduleData.jodiac_token_cost - tokenBalance} JT</p>
                      </div>
                      <button
                        onClick={() => navigate(`/${id}/settings/token-balance`)}
                        className="w-full py-4 bg-red-500 text-white rounded-2xl font-black text-sm shadow-xl shadow-red-500/20 hover:bg-red-600 transition-all active:scale-95"
                      >
                        Bakiye Yükle
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 text-gray-400">
                    <FiShield size={12} />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Güvenli İşlem</span>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Description Card */}
            {moduleData.description && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white border border-gray-100 rounded-[2rem] p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1.5 h-6 bg-[#379cab] rounded-full" />
                  <h3 className="text-lg font-black text-gray-900">Modül Hakkında</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed font-normal whitespace-pre-line">
                  {moduleData.description}
                </p>
              </motion.div>
            )}

          </div>
        </div>
      </div>
      <Footer uuid={id} />
    </div>
  );
}

